import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import CanonicalUrl from "@/components/seo/CanonicalUrl";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Industries from "@/pages/Industries";
import IndustryDetail from "@/pages/IndustryDetail";
import Solutions from "@/pages/Solutions";
import SolutionDetail from "@/pages/SolutionDetail";
import TechnologyDetail from "@/pages/TechnologyDetail";
import Compliance from "@/pages/Compliance";
import ComplianceParameterReference from "@/pages/ComplianceParameterReference";
import Projects from "@/pages/Projects";
import Team from "@/pages/Team";import Contact from "@/pages/Contact";
import Tools from "@/pages/Tools";
import CustomerPortal from "@/pages/CustomerPortal";
import AccessClaim from "@/pages/AccessClaim";
import AccessAdmin from "@/pages/AccessAdmin";
import AdminLogin from "@/pages/AdminLogin";
import AdminUsers from "@/pages/AdminUsers";
// ContentCalendar (formerly Drafts page) removed Feb 2026 — replaced
// by Ideas (primary) + Calendar (scheduling view).
import ContentPlanner from "@/pages/ContentPlanner";
import BlogStudio from "@/pages/BlogStudio";
import DocumentStudio from "@/pages/DocumentStudio";
import IdeasBoard from "@/pages/IdeasBoard";
import TrashBin from "@/pages/TrashBin";
import BrandKit from "@/pages/BrandKit";
import ComposePage from "@/pages/ComposePage";
import PlannerCalendar from "@/pages/PlannerCalendar";
import Insights from "@/pages/Insights";
import InsightPost from "@/pages/InsightPost";
import ResourcePreview from "@/pages/ResourcePreview";
import SocialAnnounce from "@/pages/SocialAnnounce";
import LinkedInBanner from "@/pages/LinkedInBanner";
import HydroChat from "@/components/HydroChat";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

// 301-equivalent redirects for renamed routes (preserves the slug)
function RedirectSolutions() {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace("/solutions", "/services")} replace />;
}
function RedirectSolutionsTech() {
  const { pathname } = useLocation();
  return <Navigate to={pathname.replace("/solutions", "/services")} replace />;
}

function Shell() {
  // Every page mounts a dark hero band that the nav sits over, so the nav
  // always renders in its dark colour scheme. (Previously this was a long
  // per-route allow-list; the migration unified all pages on the same shell.)
  const { pathname } = useLocation();
  // PDF preview is intentionally chrome-less: own header, no main nav,
  // no footer, no floating Hidro launcher. Same applies to the access
  // gate landing/admin pages — they have their own focused chrome.
  const chromeless =
    pathname.startsWith("/insights/resource/") ||
    pathname.startsWith("/access/") ||
    pathname.startsWith("/portal/access") ||
    pathname.startsWith("/portal/admins") ||
    pathname.startsWith("/portal/content-calendar") ||
    pathname.startsWith("/portal/content-planner") ||
    pathname.startsWith("/portal/content-studio") ||
    pathname.startsWith("/portal/workspace") ||
    pathname.startsWith("/portal/brand") ||
    pathname.startsWith("/admin/login") ||
    pathname.startsWith("/social/");
  return (
    <>
      <ScrollToTop />
      <CanonicalUrl />
      {!chromeless && <Nav forceDark={true} />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/industries/:slug" element={<IndustryDetail />} />
        <Route path="/services" element={<Solutions />} />
        <Route path="/services/:slug" element={<SolutionDetail />} />
        <Route path="/services/:slug/tech/:techSlug" element={<TechnologyDetail />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/compliance/:slug" element={<ComplianceParameterReference />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/team" element={<Team />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/tools" element={<Tools />} />
        <Route path="/tools/:groupId" element={<Tools />} />
        <Route path="/tools/:groupId/:toolId" element={<Tools />} />
        <Route path="/insights/resource/:slug" element={<ResourcePreview />} />
        <Route path="/insights/:slug" element={<InsightPost />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/portal" element={<CustomerPortal />} />
        <Route path="/portal/access" element={<AccessAdmin />} />
        <Route path="/portal/admins" element={<AdminUsers />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/portal/content-calendar" element={<Navigate to="/portal/workspace/ideas" replace />} />
        <Route path="/portal/content-calendar/plan/:sourceType/:slug" element={<ContentPlanner />} />
        <Route path="/portal/content-studio/blog" element={<BlogStudio />} />
        <Route path="/portal/content-studio/docs" element={<DocumentStudio />} />
        <Route path="/portal/workspace/ideas" element={<IdeasBoard />} />
        <Route path="/portal/workspace/trash" element={<TrashBin />} />
        <Route path="/portal/brand" element={<BrandKit />} />
        <Route path="/portal/content-planner" element={<ComposePage />} />
        <Route path="/portal/content-planner/calendar" element={<PlannerCalendar />} />
        {/* Legacy Drafts page — killed. Redirect anyone landing here to
            Ideas (the new primary surface). Calendar still uses the
            same data; the underlying drafts collection isn't removed. */}
        <Route path="/portal/content-planner/drafts" element={<Navigate to="/portal/workspace/ideas" replace />} />
        <Route path="/access/claim" element={<AccessClaim />} />
        <Route path="/social/announce" element={<SocialAnnounce />} />
        <Route path="/social/linkedin-banner" element={<LinkedInBanner />} />
        {/* Legacy aliases — keep working URLs that may be linked externally */}
        <Route path="/blog" element={<Navigate to="/insights" replace />} />
        <Route path="/resources" element={<Navigate to="/insights" replace />} />
        <Route path="/portfolio" element={<Navigate to="/projects" replace />} />
        <Route path="/what-we-do" element={<Navigate to="/services" replace />} />
        <Route path="/solutions" element={<Navigate to="/services" replace />} />
        <Route path="/solutions/:slug" element={<RedirectSolutions />} />
        <Route path="/solutions/:slug/tech/:techSlug" element={<RedirectSolutionsTech />} />
        <Route path="/clients" element={<Navigate to="/projects" replace />} />
        <Route path="/login" element={<Navigate to="/portal" replace />} />
        {/* ─── Wix migration redirects ──────────────────────────────────────
            These match the URLs that the old Wix site at hidroverse.co.za
            had indexed by Google. Same domain → same authority transfers;
            React Router redirects are treated by modern crawlers as 301-
            equivalent when consistent and persistent. Sitemap.xml lists
            ONLY the new canonical URLs so Google re-crawls and updates
            its index over the following 4-6 weeks. */}
        <Route path="/purification" element={<Navigate to="/services/purification" replace />} />
        <Route path="/irrigation" element={<Navigate to="/services/irrigation" replace />} />
        <Route path="/harvesting" element={<Navigate to="/services/harvesting" replace />} />
        <Route path="/poolware" element={<Navigate to="/services/pool-spa" replace />} />
        <Route path="/pumps-accessories" element={<Navigate to="/services/pumps" replace />} />
        <Route path="/water-metering" element={<Navigate to="/services/monitoring" replace />} />
        <Route path="/gutters" element={<Navigate to="/services/harvesting" replace />} />
        <Route path="/subscriptions" element={<Navigate to="/services/service" replace />} />
        <Route path="/waterproofing" element={<Navigate to="/services" replace />} />
        <Route path="/financing" element={<Navigate to="/services/rentals" replace />} />
        {/* Stale Wix URLs flagged in Bing Webmaster Tools — point them at the
            most relevant new equivalents so search engines update the index. */}
        <Route path="/products-services" element={<Navigate to="/services" replace />} />
        <Route path="/online-store-comming-soon" element={<Navigate to="/contact" replace />} />
        <Route path="/online-store" element={<Navigate to="/contact" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!chromeless && <HydroChat />}
      {!chromeless && <Footer />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <Shell />
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
